import { format, parseISO } from "date-fns";
import { ar, es, fr, ht, pt, zhCN } from "date-fns/locale";
import { resolveLocale } from "#/shared/composables/useI18n";

// Haitian Creole has no CLDR data in browsers (Intl silently falls back), so
// its date names are hand-supplied. Sourced from config/locales/dates.ht.yml —
// keep the two in sync (the legacy bundles' moment_locales/ht.js carries the
// same names).
const HT_DATE_NAMES = {
  weekday: {
    long: ["dimanch", "lendi", "madi", "mèkredi", "jedi", "vandredi", "samdi"],
    short: ["dim", "len", "mad", "mèk", "jed", "van", "sam"],
  },
  month: {
    long: ["janvye", "fevriye", "mas", "avril", "me", "jen", "jiyè", "out", "septanm", "oktòb", "novanm", "desanm"],
    short: ["jan", "fev", "mas", "avr", "me", "jen", "jiy", "out", "sep", "okt", "nov", "des"],
  },
};

// Kreyòl reads day-before-month like French ("15 jan") and keeps the 12-hour
// clock with English meridiem markers, per config/locales/dates.ht.yml. Covers
// the option shapes the app uses (weekday, day, month, year, hour, minute);
// anything else would need a new entry here rather than Intl.
function formatHaitianCreole(date, options = {}) {
  const parts = [];

  if (options.weekday) {
    parts.push(HT_DATE_NAMES.weekday[options.weekday === "short" ? "short" : "long"][date.getDay()]);
  }
  if (options.day) {
    parts.push(String(date.getDate()));
  }
  if (options.month) {
    parts.push(HT_DATE_NAMES.month[options.month === "short" ? "short" : "long"][date.getMonth()]);
  }
  if (options.year) {
    parts.push(String(date.getFullYear()));
  }
  if (options.hour) {
    const minutes = options.minute ? `:${String(date.getMinutes()).padStart(2, "0")}` : "";

    parts.push(`${date.getHours() % 12 || 12}${minutes} ${date.getHours() < 12 ? "AM" : "PM"}`);
  }

  return parts.join(" ");
}

// Restates the date's own wall clock — the fields its getters report, which a
// `TZDate` reports in the offset it was parsed in — as the same fields in UTC.
//
// Intl formats an instant in a named zone, so rendering a slot's local time
// would otherwise mean passing its UTC offset as `timeZone`. Offset zone ids
// ("-05:00") are a recent addition to ECMA-402 that older ICU builds reject
// with a RangeError, and `Intl.DateTimeFormat` is also the one thing here that
// reads the runtime's own zone. Formatting the restated instant in UTC needs
// neither: every runtime supports it, and the output no longer depends on where
// the code runs.
function toUTCWallClock(date) {
  return new Date(
    Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes(),
      date.getSeconds(),
      date.getMilliseconds(),
    ),
  );
}

// Formats a Date in the active `?locale=` using the browser-native Intl API.
// Preferred over moment for localized display: no bundled locale data, no global
// locale state, locale-natural ordering, and coverage for every language the
// browser's CLDR knows. Haitian Creole is the exception — no browser ships `ht`
// data, so it formats from the hand-supplied table above.
//
// The date carries its own zone: pass a `TZDate` to render a slot's local time,
// a plain Date to render the browser's. `options` is the Intl option bag minus
// `timeZone`, which is set here.
//
// Pass an already-parsed Date — callers that start from a date-only string
// ("2021-09-01") must parse it in local time first (e.g. parseISO(str)), since
// `new Date("2021-09-01")` parses as UTC and can shift the day.
export function formatLocalizedDate(date, options) {
  // Intl's `format` expects a Date (or timestamp) and throws "Invalid time
  // value" for an unparseable one — in the English-fallback path too — so
  // coerce once and guard before formatting, and format the same coerced value
  // the guard validated.
  const parsed = date instanceof Date ? date : new Date(date);

  if (Number.isNaN(parsed.getTime())) {
    return "";
  }

  const locale = resolveLocale();

  // Reads the wall clock through the date's own getters, so it needs no
  // restating.
  if (locale === "ht") {
    return formatHaitianCreole(parsed, options);
  }

  const wallClock = toUTCWallClock(parsed);
  const intlOptions = { ...options, timeZone: "UTC" };

  try {
    // The locale list makes Intl itself fall back to English for locales the
    // runtime has no data for — those don't throw, they'd silently use the
    // runtime default locale if passed alone.
    return new Intl.DateTimeFormat([locale, "en"], intlOptions).format(wallClock);
  } catch {
    return new Intl.DateTimeFormat("en", intlOptions).format(wallClock);
  }
}

// Formats a date, adding `year` to `options` on the terms `showYear` sets: "never", "next_year"
// (any year but the current one) or "always". An unrecognized value states no year.
export function formatDateWithRelevantYear(date, options, showYear) {
  const parsed = toComparableDate(date);

  if (Number.isNaN(parsed.getTime())) {
    return "";
  }

  if (!statesYear(parsed, showYear)) {
    return formatLocalizedDate(parsed, options);
  }

  return formatLocalizedDate(parsed, { ...options, year: "numeric" });
}

// A `TZDate` is a `Date`, so it passes through keeping its own offset. `new Date("2027-01-01")`
// reads a date-only string as UTC, which reports the previous year in a negative offset and would
// then omit the very year this exists to state; `parseISO` reads it as local midnight.
function toComparableDate(date) {
  if (date instanceof Date) {
    return date;
  }

  return typeof date === "string" ? parseISO(date) : new Date(date);
}

// Reads `date` through the same getters `formatLocalizedDate` renders from, so a `TZDate` yields
// the year of its own offset. The year it is compared against is the viewer's, which is what makes
// the two agree on "this year" for a viewer reading a clinic's calendar.
function statesYear(date, showYear) {
  if (showYear === "always") {
    return true;
  }

  if (showYear === "next_year") {
    return date.getFullYear() !== new Date().getFullYear();
  }

  return false;
}

// Formats the time of day in the active `?locale=`. Locales differ on the clock
// itself and not only on the words for it — French and Spanish read 17:00 where
// English reads 5 PM — so the whole time goes through Intl rather than a fixed
// 12-hour pattern whose meridiem gets translated.
//
// Pass `{ minute: undefined }` for an on-the-hour time to render "5 PM" rather
// than "5:00 PM".
export function formatLocalizedTime(date, options) {
  return formatLocalizedDate(date, { hour: "numeric", minute: "2-digit", ...options });
}

// date-fns locales for the Sparkle locales other than English (`shared/lib/locale.js`).
const DATE_LOCALES = Object.freeze({ ar, es, fr, ht, pt, "zh-CN": zhCN });

// Per-locale adjustments that keep the wording of the legacy moment "dddd, MMMM Do".
const ORDINAL_DATE_STYLES = Object.freeze({
  ar: { finish: (text) => text.replace(/\d/g, (digit) => "٠١٢٣٤٥٦٧٨٩"[digit]).replace(/,/g, "،") },
  ht: { day: "d" },
  pt: { finish: (text) => text.charAt(0).toUpperCase() + text.slice(1) },
});

/**
 * Returns the weekday and ordinal date in the active `?locale=`: `"Thursday, October 1st"`,
 * `"jeudi, octobre 1er"`, `"الخميس، أكتوبر ١"`. Adds the year on the terms `showYear` sets.
 *
 * @param {Date} date
 * @param {String} showYear "never", "next_year" or "always"
 * @returns {String}
 */
export function formatOrdinalDate(date, showYear) {
  const locale = resolveLocale();
  const style = ORDINAL_DATE_STYLES[locale] ?? {};
  const year = statesYear(date, showYear) ? ", yyyy" : "";
  const text = format(date, `EEEE, MMMM ${style.day ?? "do"}${year}`, { locale: DATE_LOCALES[locale] });

  return style.finish ? style.finish(text) : text;
}

/**
 * Formats a date with a date-fns `pattern` in the active `?locale=`, switching to `patternWithYear` on the terms
 * `showYear` sets: `formatDatePattern("2026-10-06", "MMM d", "MMM d, yyyy")` → `"Oct 6"`, `"oct. 6"` in French.
 *
 * @param {Date|String} date a Date, or a date-only string read as local midnight
 * @param {String} pattern
 * @param {String} [patternWithYear] defaults to `pattern`
 * @param {String} [showYear] "never" (default), "next_year" or "always"
 * @returns {String}
 */
export function formatDatePattern(date, pattern, patternWithYear = pattern, showYear = "never") {
  const parsed = toComparableDate(date);

  if (Number.isNaN(parsed.getTime())) {
    return "";
  }

  const locale = resolveLocale();
  return format(parsed, statesYear(parsed, showYear) ? patternWithYear : pattern, { locale: DATE_LOCALES[locale] });
}
